import AddYourCompany from "assets/navigationIcons/add-company.svg?react";
import SelectYourAccount from "assets/navigationIcons/select-account.svg?react";
import {
  ADD_COMPANY,
  ADD_PARTNER,
  NEW_USER,
  INCOMLEND_INTERNAL,
  ONBOARDED,
  ONBOARDING,
  PRESALES,
  REGISTERED,
  SELECT_ACCOUNT,
  TP_ADDED,
  BOOK_MEETING,
} from "codes";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "redux/hooks";
import { menuStyling as styling } from "styles/theme";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import {
  isActingAsClientAccount,
  isInternalViewActive,
} from "helpers/viewContext";
import { normalizeRoleName } from "helpers/normalizeRoleName";
import { PERMISSIONS } from "permissions";
import { useSearchParams } from "react-router-dom";
import { AGENT_USER } from "./agentMenu";
import ClientMenuItems, { getOnboardedClientMenuItems } from "./client";
import { INVESTOR_USER_New } from "./investorMenu";
import INTERNAL_MENUS from "./incomlend-internal-menus";
import { getPresalesMenuItems } from "./presalesMenu";

interface Item {
  title: string;
  icon: any;
  activeColor: string;
  iconActiveColor: string;
  iconInactiveColor: string;
  circularBackground: boolean;
  link: string;
  strict?: boolean;
  items?: Item[];
  permission?: string;
  disabled?: boolean;
}

const menuItems = () => {
  const APP_PERMISSION = useAppSelector((state) => state.appState.permission);
  const USER_PERMISSIONS = useAppSelector(
    (state) => state.appState.user_permission,
  );
  const APP_PERMISSIONS = useAppSelector((state) => state.appState.permissions);
  const { data: curr_user } = useGetCurrentUserQuery();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const APP_STATE = useAppSelector((state) => state.appState.value);
  const clientMenu = ClientMenuItems(curr_user?.access_marketplace);

  // Check if we're on investor dashboard with funder_id
  const funderId = searchParams.get("funder_id");
  const isInvestorDashboardWithFunder =
    funderId && window.location.pathname.includes("/investor/");
  // FORCE INVESTOR MENU when on any investor route with funder_id
  if (isInvestorDashboardWithFunder) {
    const investorMenu = INVESTOR_USER_New(t, styling, funderId);
    return investorMenu;
  }

  const filterCondition = (item: Item) => {
    if (!item.permission) return true;
    // Support multiple permissions separated by |
    if (item.permission.includes("|")) {
      const perms = item.permission.split("|");
      // Check if any of the permissions are in USER_PERMISSIONS
      const hasPermission = perms.some((perm) =>
        USER_PERMISSIONS.includes(perm),
      );
      // Check if any of the permissions are group names in APP_PERMISSIONS
      const hasGroup = perms.some((perm) => APP_PERMISSIONS.includes(perm));
      return hasPermission || hasGroup;
    }
    // Check if the permission is in USER_PERMISSIONS
    const hasPermission = USER_PERMISSIONS.includes(item.permission as string);
    // Check if the permission is a group name in APP_PERMISSIONS
    const hasGroup = APP_PERMISSIONS.includes(item.permission as string);
    return hasPermission || hasGroup;
  };

  const filterItem = (item: Item) => {
    if (item.items) {
      const filteredItems = item.items.filter(filterCondition);
      if (!filteredItems.length) return false;
      return {
        ...item,
        items: filteredItems,
      };
    }
    if (filterCondition(item)) return item;
    return false;
  };

  const INTERNAL_MENU_ITEMS = INTERNAL_MENUS.map(filterItem).filter(
    (x) => x,
  ) as Item[];

  const normalizedRole = normalizeRoleName(curr_user?.user_role);

  if (normalizedRole === "Sales_Partner") {
    return AGENT_USER(t, styling);
  }

  if (
    isInternalViewActive({ appPermission: APP_PERMISSION, appState: APP_STATE })
  ) {
    return INTERNAL_MENU_ITEMS;
  }

  // Check for risk management permission first
  if (USER_PERMISSIONS?.includes(PERMISSIONS.show_risk_management)) {
    return INTERNAL_MENU_ITEMS;
  }

  const isClientAccountContext =
    curr_user?.user_role === "Client" ||
    isActingAsClientAccount({
      appPermission: APP_PERMISSION,
      appState: APP_STATE,
    });

  // Internal users switching to a linked account (role switcher / /accounts picker)
  if (isClientAccountContext) {
    return clientMenu;
  } else if (curr_user?.user_role === "Investor") {
    return INVESTOR_USER_New(t, styling, funderId || undefined);
  } else if (APP_PERMISSION === INCOMLEND_INTERNAL) {
    return INTERNAL_MENU_ITEMS;
  }

  // Check if we're in Investor context (when accessing funders) - PRIORITY OVER APP_STATE
  if (APP_PERMISSION === "Investor") {
    const investorMenu = INVESTOR_USER_New(t, styling, funderId || undefined);
    return investorMenu;
  }

  // Only use APP_STATE if APP_PERMISSION is not "Investor"
  switch (APP_STATE) {
    case NEW_USER:
      return [
        {
          title: `${t("add-your-company")}`,
          icon: AddYourCompany,
          ...styling,
          link: ADD_COMPANY,
        },
        {
          title: `${t("Book a meeting")}`,
          icon: CalendarMonthIcon,
          ...styling,
          link: BOOK_MEETING,
        },
      ];
    case SELECT_ACCOUNT:
      return [
        {
          title: `Select Your Account`,
          icon: SelectYourAccount,
          ...styling,
          link: "/accounts",
        },
      ];
    case REGISTERED:
    case PRESALES:
    case TP_ADDED:
    case ADD_PARTNER:
      return getPresalesMenuItems(t);
    case ONBOARDING:
    case ONBOARDED:
      return getOnboardedClientMenuItems(t, curr_user?.access_marketplace);

    default:
      return [
        {
          title: `${t("add-your-company")}`,
          icon: AddYourCompany,
          ...styling,
          link: ADD_COMPANY,
        },
      ];
  }
};

export default menuItems;
